import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDatepicker } from '../hooks/useDatepicker';
import { t } from '../utils/i18n';
import { toSomaliDept } from '../utils/deptTranslations';

const REPORT_CATEGORIES = {
  personnel: {
    label: 'Personnel Reports',
    icon: '/images/personnel.png',
    sub: [
      { value: 'personnel_list',       label: 'Personnel List' },
      { value: 'personnel_department', label: 'Personnel by Department' },
      { value: 'personnel_rank',       label: 'Personnel by Rank' },
      { value: 'personnel_status',     label: 'Personnel by Status' },
    ],
  },
  payroll: {
    label: 'Payroll Reports',
    icon: '/images/payroll.png',
    sub: [
      { value: 'payroll_list',      label: 'Monthly Salary Report' },
      { value: 'payroll_bonus',     label: 'Bonus Report' },
      { value: 'payroll_allowance', label: 'Service Allowance Report' },
      { value: 'payroll_operation', label: 'Operation Cost Report' },
    ],
  },
  leave: {
    label: 'Leave Reports',
    icon: '/images/leave.png',
    sub: [
      { value: 'leave_list',       label: 'Personnel on Leave' },
      { value: 'leave_history',    label: 'Leave History' },
      { value: 'leave_department', label: 'Leave by Department' },
      { value: 'leave_type',       label: 'Leave by Leave Type' },
    ],
  },
};

const BACKEND_TYPE = {
  personnel_list:       'personnel_list',
  personnel_department: 'personnel_list',
  personnel_rank:       'personnel_list',
  personnel_status:     'personnel_list',
  payroll_list:         'payroll_list',
  payroll_bonus:        'payroll_list',
  payroll_allowance:    'payroll_list',
  payroll_operation:    'payroll_list',
  leave_list:           'leave_list',
  leave_history:        'leave_list',
  leave_department:     'leave_list',
  leave_type:           'leave_list',
};

const PERSONNEL_STATUS_OPTS = [
  { value: '', label: 'All Statuses' },
  { value: '1', label: 'Active' },
  { value: '0', label: 'Pending' },
  { value: '2', label: 'Inactive' },
];

const LEAVE_STATUS_OPTS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending',   label: 'Pending' },
  { value: 'approved',  label: 'Approved' },
  { value: 'rejected',  label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

function roleList(user) {
  return (user?.roles?.map(r => String(r.name || '').toLowerCase()) || []);
}

function derivePermissions(roles) {
  const is = (...names) => names.some(n => roles.includes(n));
  return {
    isAdmin:    is('admin', 'super_admin', 'system_admin'),
    isFinance:  is('finance', 'payroll_officer', 'finance_officer', 'finance_director'),
    isCommander:is('commander', 'manager', 'department_head', 'unit_commander', 'department_commander', 'army_commander'),
    isHR:       is('hr_officer', 'hr', 'personnel_officer', 'personnel'),
    isAuditor:  is('auditor', 'internal_auditor'),
    isDataEntry:is('data_entry_operator', 'data_entry'),
  };
}

function allowedCategories(perms) {
  if (perms.isAdmin || perms.isAuditor) return ['personnel', 'payroll', 'leave'];
  if (perms.isFinance)                  return ['payroll'];
  if (perms.isCommander || perms.isHR)  return ['personnel', 'leave'];
  return ['personnel'];
}

function canExport(perms) {
  return perms.isAdmin || perms.isFinance || perms.isCommander || perms.isHR || perms.isAuditor;
}

const PERSONNEL_COLS = [
  { key: 'army_id',        label: 'Army ID' },
  { key: 'rank',           label: 'Rank' },
  { key: 'name',           label: 'Name' },
  { key: 'force',          label: 'Force' },
  { key: 'department',     label: 'Department' },
  { key: 'sub_department', label: 'Sub-Department' },
  { key: 'organization',   label: 'Organization' },
  { key: 'status',         label: 'Status' },
];

const PAYROLL_COLS = [
  { key: 'army_id',      label: 'Army ID' },
  { key: 'name',         label: 'Name' },
  { key: 'department',   label: 'Department' },
  { key: 'rank',         label: 'Rank' },
  { key: 'salary',       label: 'Salary' },
  { key: 'allowances',   label: 'Allowances' },
  { key: 'deductions',   label: 'Deductions' },
  { key: 'net_pay',      label: 'Net Pay' },
  { key: 'payment_date', label: 'Payment Date' },
  { key: 'status',       label: 'Status' },
];

const LEAVE_COLS = [
  { key: 'army_id',    label: 'Army ID' },
  { key: 'name',       label: 'Name' },
  { key: 'force',      label: 'Force' },
  { key: 'department', label: 'Department' },
  { key: 'rank',       label: 'Rank' },
  { key: 'leave_type', label: 'Leave Type' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'end_date',   label: 'End Date' },
  { key: 'days',       label: 'Days' },
  { key: 'status',     label: 'Status' },
];

function colsForType(subType) {
  if (subType.startsWith('payroll')) return PAYROLL_COLS;
  if (subType.startsWith('leave'))   return LEAVE_COLS;
  return PERSONNEL_COLS;
}

const Reports = ({ api, user }) => {
  const roles = roleList(user);
  const perms = derivePermissions(roles);
  const cats  = allowedCategories(perms);
  const currentArmyTypeId = user?.army_personnel?.army_type?.id?.toString() || '';

  const [armyTypes,      setArmyTypes]      = useState([]);
  const [departments,    setDepartments]    = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [ranks,          setRanks]          = useState([]);
  const [leaveTypes,     setLeaveTypes]     = useState([]);

  const [activeCategory, setActiveCategory] = useState(cats[0] || 'personnel');
  const [activeSubType,  setActiveSubType]  = useState(REPORT_CATEGORIES[cats[0] || 'personnel'].sub[0].value);
  const [reportData,     setReportData]     = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [generated,      setGenerated]      = useState(false);

  const [filters, setFilters] = useState({
    army_type_id:      perms.isAdmin ? '' : currentArmyTypeId,
    department_id:     '',
    sub_department_id: '',
    organization_unit: '',
    rank:              '',
    status:            '',
    date_from:         '',
    date_to:           '',
    leave_type_id:     '',
  });

  const deptRef       = useRef(null);
  const subDeptRef    = useRef(null);
  const rankRef       = useRef(null);
  const dateFromRef   = useRef(null);
  const dateToRef     = useRef(null);

  const visibleDepts = useMemo(() => {
    let depts = filters.army_type_id
      ? departments.filter(d => String(d.army_type_id) === String(filters.army_type_id))
      : departments;
    const seenNames = new Set();
    return depts.filter(d => {
      const displayName = toSomaliDept(d.name).trim();
      if (!displayName || seenNames.has(displayName)) return false;
      seenNames.add(displayName);
      return true;
    });
  }, [filters.army_type_id, departments]);

  const visibleSubDepts = useMemo(() => {
    let subDepts = [];
    if (filters.department_id) {
      subDepts = subDepartments.filter(sd => String(sd.parent_id) === String(filters.department_id));
    } else if (filters.army_type_id) {
      const validDeptIds = new Set(
        departments
          .filter(d => String(d.army_type_id) === String(filters.army_type_id))
          .map(d => String(d.id))
      );
      subDepts = subDepartments.filter(sd => validDeptIds.has(String(sd.parent_id)) || String(sd.army_type_id) === String(filters.army_type_id));
    } else {
      subDepts = subDepartments;
    }
    const seenNames = new Set();
    return subDepts.filter(sd => {
      const displayName = toSomaliDept(sd.name).trim();
      if (!displayName || seenNames.has(displayName)) return false;
      seenNames.add(displayName);
      return true;
    });
  }, [filters.department_id, filters.army_type_id, departments, subDepartments]);

  useEffect(() => {
    setFilters(prev => ({ ...prev, department_id: '', sub_department_id: '' }));
  }, [filters.army_type_id]);

  useEffect(() => {
    setFilters(prev => ({ ...prev, sub_department_id: '' }));
  }, [filters.department_id]);



  const handleDateChange = (a, b) => {
    if (a && a.target) {
      setFilters(prev => ({ ...prev, [a.target.name]: a.target.value }));
    } else if (typeof a === 'string') {
      setFilters(prev => ({ ...prev, [a]: b }));
    }
  };
  useDatepicker(handleDateChange);

  useEffect(() => {
    fetchMeta();
  }, []);

  const fetchMeta = async () => {
    try {
      const [rpt, lt] = await Promise.all([
        api.get('/reports'),
        api.get('/leave_types'),
      ]);
      setArmyTypes(rpt.data.army_types || []);
      setDepartments(rpt.data.departments || []);
      setSubDepartments(rpt.data.sub_departments || []);
      setRanks(rpt.data.ranks || []);
      setLeaveTypes(lt.data.leave_types || []);
    } catch (e) {
      console.error('Reports meta fetch error', e);
    }
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    const firstSub = REPORT_CATEGORIES[cat].sub[0].value;
    setActiveSubType(firstSub);
    setReportData([]);
    setGenerated(false);
  };

  const handleSubTypeChange = (sub) => {
    setActiveSubType(sub);
    setReportData([]);
    setGenerated(false);
  };

  const generateReport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGenerated(false);
    try {
      const backendType = BACKEND_TYPE[activeSubType] || 'personnel_list';
      const sendFilters = {
        army_type_id:      perms.isAdmin ? filters.army_type_id : currentArmyTypeId,
        department_id:     filters.department_id || '',
        sub_department_id: filters.sub_department_id || '',
        organization_unit: filters.organization_unit || '',
        rank:              filters.rank || '',
        status:            filters.status,
        date_from:         filters.date_from || '',
        date_to:           filters.date_to || '',
        leave_type_id:     filters.leave_type_id,
      };

      const { data } = await api.post('/reports/generate', {
        report: { report_type: backendType, filters: sendFilters }
      });

      const rows = data?.data?.data || data?.data || data || [];
      setReportData(rows);
      setGenerated(true);
    } catch (err) {
      console.error('Report generation error', err);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!reportData.length) return alert('Generate a report first.');
    if (!canExport(perms))  return alert('You do not have permission to export.');

    const cols = colsForType(activeSubType);
    const header = cols.map(c => c.label).join(',');
    const rows = reportData.map(row =>
      cols.map(c => {
        let val = row[c.key] ?? 'N/A';
        if (c.key === 'department' || c.key === 'sub_department') {
          val = toSomaliDept(val);
        }
        return String(val).replace(/,/g, ' ');
      }).join(',')
    );
    const csv  = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${activeSubType}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isLeave     = activeSubType.startsWith('leave');
  const isPayroll   = activeSubType.startsWith('payroll');
  const statusOpts  = isLeave ? LEAVE_STATUS_OPTS : PERSONNEL_STATUS_OPTS;
  const cols        = colsForType(activeSubType);

  return (
    <div className="mainContent">
      <div className="mainContentCT dBlock">

        <div className="dFlex baseTableHd mb_16">
          <h3 className="txt20 fwMedium colorBlack">{t('reports')}</h3>
          {canExport(perms) && generated && (
            <div className="dFlex">
              <button className="tblTopBtn" onClick={exportCSV}>
                <span>{t('export_data') || 'Export CSV'}</span>
                <img src="/images/export.png" alt="" />
              </button>
            </div>
          )}
        </div>

        <div className="twoEqlColTbl">

          <div className="baseTableWrap twoEqlColTblCh" style={{ minWidth: 220, maxWidth: 260 }}>
            <div className="baseTableHd mb_16">
              <h4 className="txt16 fwMedium colorBlack">Report Type</h4>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {cats.map(cat => (
                <li key={cat} style={{ marginBottom: 4 }}>
                  <button
                    className={`tab w-100 text-start${activeCategory === cat ? ' active' : ''}`}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontWeight: 500 }}
                    onClick={() => handleCategoryChange(cat)}
                  >
                    {REPORT_CATEGORIES[cat].label}
                  </button>
                  {activeCategory === cat && (
                    <ul style={{ listStyle: 'none', padding: '4px 0 4px 16px', margin: 0 }}>
                      {REPORT_CATEGORIES[cat].sub.map(sub => (
                        <li key={sub.value}>
                          <button
                            className={`optBtn${activeSubType === sub.value ? ' active' : ''}`}
                            style={{ padding: '5px 8px', width: '100%', textAlign: 'left', fontWeight: activeSubType === sub.value ? 600 : 400 }}
                            onClick={() => handleSubTypeChange(sub.value)}
                          >
                            {sub.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="baseTableFormSec twoEqlColTblCh">
            <div className="dFlex baseTableHd mb_8">
              <h4 className="txt16 fwMedium colorBlack">
                {REPORT_CATEGORIES[activeCategory]?.sub.find(s => s.value === activeSubType)?.label}
              </h4>
            </div>
            <form onSubmit={generateReport}>
              <ul className="dFlex formFieldsWrap">

                <li className="w_1_1">
                  <div className="formInpt">
                    <label className="mb_10">{t('army_type') || 'Force'}</label>
                    <div className="dFlex radioSet">
                      {perms.isAdmin && (
                        <label className="customRadio">
                          <input type="radio" name="army_type_id" value=""
                            checked={filters.army_type_id === ''}
                            onChange={() => setFilters(prev => ({ ...prev, army_type_id: '' }))} />
                          <span className="checkmark"></span>
                          All Forces
                        </label>
                      )}
                      {armyTypes.map(at => (
                        <label key={at.id} className="customRadio">
                          <input type="radio" name="army_type_id" value={at.id}
                            checked={filters.army_type_id === String(at.id)}
                            onChange={() => setFilters(prev => ({ ...prev, army_type_id: String(at.id) }))}
                            disabled={!perms.isAdmin && currentArmyTypeId && String(at.id) !== currentArmyTypeId}
                          />
                          <span className="checkmark"></span>
                          {at.name}
                        </label>
                      ))}
                    </div>
                  </div>
                </li>

                <li className="w_1_2">
                  <div className="formInpt">
                    <label className="mb_10">{t('department')}</label>
                    <select key={`dept-${filters.army_type_id}`} ref={deptRef} value={filters.department_id}
                      onChange={e => setFilters(prev => ({ ...prev, department_id: e.target.value }))}
                      style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <option value="">All Departments</option>
                      {visibleDepts.map(d => (
                        <option key={d.id} value={d.id}>{toSomaliDept(d.name)}</option>
                      ))}
                    </select>
                  </div>
                </li>

                <li className="w_1_2">
                  <div className="formInpt">
                    <label className="mb_10">Sub Department</label>
                    <select key={`subdept-${filters.army_type_id}-${filters.department_id}`} ref={subDeptRef} value={filters.sub_department_id}
                      onChange={e => setFilters(prev => ({ ...prev, sub_department_id: e.target.value }))}
                      style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <option value="">All Sub Departments</option>
                      {visibleSubDepts.map(sd => (
                        <option key={sd.id} value={sd.id}>{toSomaliDept(sd.name)}</option>
                      ))}
                    </select>
                  </div>
                </li>

                <li className="w_1_2">
                  <div className="formInpt">
                    <label className="mb_10">Organization Unit</label>
                    <input type="text" value={filters.organization_unit}
                      onChange={e => setFilters(prev => ({ ...prev, organization_unit: e.target.value }))}
                      placeholder="Enter organization unit" />
                  </div>
                </li>

                {!isPayroll && (
                  <li className="w_1_2">
                    <div className="formInpt">
                      <label className="mb_10">{t('rank')}</label>
                      <select ref={rankRef} value={filters.rank}
                        onChange={e => setFilters(prev => ({ ...prev, rank: e.target.value }))}>
                        <option value="">All Ranks</option>
                        {ranks.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </li>
                )}

                <li className="w_1_2">
                  <div className="formInpt">
                    <label className="mb_10">{t('status')}</label>
                    <select value={filters.status}
                      onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}>
                      {statusOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </li>

                {isLeave && (
                  <li className="w_1_2">
                    <div className="formInpt">
                      <label className="mb_10">Leave Type</label>
                      <select value={filters.leave_type_id}
                        onChange={e => setFilters(prev => ({ ...prev, leave_type_id: e.target.value }))}>
                        <option value="">All Leave Types</option>
                        {leaveTypes.map(lt => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
                      </select>
                    </div>
                  </li>
                )}

                <li className="w_1_4">
                  <div className="formInpt hasDatePicker">
                    <label className="mb_10">{t('date_range_from') || 'From'}</label>
                    <input ref={dateFromRef} name="date_from" type="text"
                      className="customDatepicker" placeholder="Select Date"
                      value={filters.date_from} onChange={handleDateChange} />
                  </div>
                </li>
                <li className="w_1_4">
                  <div className="formInpt hasDatePicker">
                    <label className="mb_10">{t('to') || 'To'}</label>
                    <input ref={dateToRef} name="date_to" type="text"
                      className="customDatepicker" placeholder="Select Date"
                      value={filters.date_to} onChange={handleDateChange} />
                  </div>
                </li>

              </ul>
              <div className="dFlex mt_16 btnSet">
                <button type="submit" className="btn" disabled={loading}>
                  {loading ? (t('loading') || 'Loading...') : (t('generate_report') || 'Generate Report')}
                </button>
                {generated && (
                  <button type="button" className="btnSecondary ms-2" onClick={() => { setReportData([]); setGenerated(false); }}>
                    Clear
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {generated && (
          <div className="mt_40 baseTableWrap">
            <div className="dFlex baseTableHd mb_16">
              <h3 className="txt20 fwMedium colorBlack">
                {REPORT_CATEGORIES[activeCategory]?.sub.find(s => s.value === activeSubType)?.label}
                {' '}
                <span className="txt14" style={{ color: '#888' }}>({reportData.length} records)</span>
              </h3>
              {canExport(perms) && (
                <button className="tblTopBtn" onClick={exportCSV}>
                  <span>{t('export_data') || 'Export CSV'}</span>
                  <img src="/images/export.png" alt="" />
                </button>
              )}
            </div>
            <div className="baseTableContainer">
              <table className="baseTable baseTableDepartment">
                <tbody>
                  <tr>
                    {cols.map(c => <th key={c.key}>{c.label}</th>)}
                  </tr>
                  {reportData.length > 0 ? reportData.map((row, i) => (
                    <tr key={i}>
                      {cols.map(c => {
                        let val = row[c.key] ?? 'N/A';
                        if (c.key === 'department' || c.key === 'sub_department') {
                          val = toSomaliDept(val);
                        }
                        return <td key={c.key}>{val}</td>;
                      })}
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={cols.length} style={{ textAlign: 'center', padding: '32px 0', color: '#888' }}>
                        <div style={{ fontSize: 15 }}>No records found matching your query.</div>
                        <div style={{ fontSize: 13, marginTop: 4 }}>Try adjusting the filters and generate again.</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Reports;
